import { AMP_SETTINGS, MultipleDevicesFindingSettings } from '@ampsec/platform-client';

/**
 * Helpers for the "Multiple devices per user" tenant setting (APD-1880).
 *
 * The setting is an object rather than a bare boolean so the threshold is
 * tunable without a release, which means the toggle has to read-modify-write
 * instead of just writing `true`.
 */

const DEFAULTS = AMP_SETTINGS.tenant.MULTIPLE_DEVICES_SETTINGS.defaultValue;

/**
 * Normalises whatever the settings API returns into a usable object.
 *
 * A tenant that has never touched this setting returns nothing, and a tenant
 * whose stored value predates a field returns it partially, so both are filled
 * from the defaults rather than trusted to be complete.
 */
export const parseMultipleDevicesSettings = (value: unknown): MultipleDevicesFindingSettings => {
    const isObject = value !== null && typeof value === 'object';
    const stored = (isObject ? value : {}) as Partial<MultipleDevicesFindingSettings>;

    return {
        enabled: typeof stored.enabled === 'boolean' ? stored.enabled : DEFAULTS.enabled,
        deviceThreshold:
            typeof stored.deviceThreshold === 'number' && Number.isFinite(stored.deviceThreshold)
                ? stored.deviceThreshold
                : DEFAULTS.deviceThreshold,
    };
};

/**
 * Builds the value the toggle writes back. The rest of the object is carried
 * over so flipping the switch cannot reset a threshold the tenant has tuned.
 */
export const withMultipleDevicesEnabled = (
    current: MultipleDevicesFindingSettings,
    enabled: boolean,
): MultipleDevicesFindingSettings => ({ ...current, enabled });
